import React, { useState, useEffect, useRef } from 'react';
import axios from '../../axios';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import './BrokerAgreement.core.css';
import './BrokerAgreement.editor.css';
import './BrokerAgreement.buttons.css';
import './BrokerAgreement.cards.css';
import './BrokerAgreement.contact.css';
import './BrokerAgreement.responsive.css';
import './BrokerAgreement.print.css';
import './BrokerAgreement.layout.css';
import SpinnerOverlay from '../Spinner/spinneroverlay'; // Import SpinnerOverlay component
import { useReactToPrint } from 'react-to-print';
import config from "../../config"; // Import config for backend_url
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faMapMarkerAlt, faPhone, faEnvelope, faHome } from '@fortawesome/free-solid-svg-icons';

// Add print-friendly styles and spinner animation
const styles = `
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media print {
    /* Hide everything initially */
    body * {
      visibility: hidden;
    }
    
    /* Set up page format */
    @page {
      size: A4;
      margin: 0;
    }
    
    /* Position the container */
    .broker-agreement-container {
      position: absolute;
      left: 0;
      top: 0;
      width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
      box-shadow: none !important;
      border: none !important;
      background-color: white;
    }
    
    /* Make only the agreement content and signature section visible */
    .agreement-content, .signature-section {
      visibility: visible !important;
      display: block !important;
      position: relative !important;
      page-break-inside: avoid;
      padding: 2cm !important;
      margin: 0 !important;
      border: none !important;
      font-size: 12pt !important;
      line-height: 1.5 !important;
      color: black !important;
      background-color: white !important;
      box-shadow: none !important;
    }
    
    /* Typography improvements for print */
    .agreement-content *, .signature-section * {
      visibility: visible !important;
      font-family: 'Arial', 'Helvetica', sans-serif !important;
      color: black !important;
    }
    
    /* Style headings in the printed document */
    .agreement-content h1, .agreement-content h2, .agreement-content h3,
    .signature-section h1, .signature-section h2, .signature-section h3 {
      margin-top: 1cm !important;
      margin-bottom: 0.5cm !important;
      page-break-after: avoid !important;
      font-weight: bold !important;
    }
    
    /* Style paragraphs */
    .agreement-content p, .signature-section p {
      margin-bottom: 0.3cm !important;
      orphans: 3 !important;
      widows: 3 !important;
    }
    
    /* Fix for lists */
    .agreement-content ul, .agreement-content ol,
    .signature-section ul, .signature-section ol {
      margin-left: 0.5cm !important;
      margin-bottom: 0.5cm !important;
    }
    
    /* Special styling for signature area */
    .signature-section {
      margin-top: 1cm !important;
      border-top: 1px solid #ccc !important;
      padding-top: 1cm !important;
    }
    
    /* Adjust signature lines */
    .signature-section div[style*="borderBottom"] {
      border-bottom: 1px solid black !important;
    }
    
    /* Style signature elements */
    .signature-section div[style*="textAlign: center"] {
      margin-bottom: 0.5cm !important;
    }
    
    /* Fixes for signature section flex layout */
    .signature-section div[style*="display: flex"] {
      display: flex !important;
      justify-content: space-between !important;
    }
    
    /* Document hash and verification info */
    .signature-section div[style*="borderTop"] {
      border-top: 1px solid #ccc !important;
      margin-top: 1cm !important;
      padding-top: 0.5cm !important;
      font-size: 9pt !important;
    }
    
    /* Hide everything else */
    .no-print, 
    .broker-agreement-container > div:not(.agreement-content):not(.signature-section),
    h2, 
    button {
      display: none !important;
    }
  }
`;

function BrokerAgreement({ conversationId }) {
  const [agreement, setAgreement] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false); // Add new state for generating agreement
  const [isPrinting, setIsPrinting] = useState(false); // Add state for printing process
  const [brokerDescription, setBrokerDescription] = useState('');
  const [brokerId, setBrokerId] = useState(null); // New state for broker id
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [sellerId, setSellerId] = useState(null); // Add state for sellerId
  const [propertyDesignation, setPropertyDesignation] = useState(''); // New state for property designation
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // New state to track if device is mobile
  const backend_url = config.backend_url; // Define backend_url from config
  const [userIsLoggedIn, setUserIsLoggedIn] = useState(false); // Add state for user login status
  const [showRegenerateModal, setShowRegenerateModal] = useState(false); // New state for regenerate confirmation modal
  const [hasContentChanged, setHasContentChanged] = useState(false); // New state for tracking content changes
  const [modalPropertyDesignation, setModalPropertyDesignation] = useState(''); // New state for modal property designation
  
  const [sellerInfo, setSellerInfo] = useState(null);
  const [sellerData, setSellerData] = useState(null);
  
  // Add ref to track scroll position
  const scrollPositionRef = useRef(0);
  const editorRef = useRef(null);
  const normalizationQuillRef = useRef(null); // New ref for content normalization
  const [preserveScrollAfterChange, setPreserveScrollAfterChange] = useState(false);
  
  // Check if user is logged in
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userTypeValue = localStorage.getItem('userType');
    setUserIsLoggedIn(!!userId && !!userTypeValue);
  }, []);

  // Track window size changes for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set modal property designation when regenerate modal opens
  useEffect(() => {
    if (showRegenerateModal && agreement?.property_designation) {
      setModalPropertyDesignation(agreement.property_designation);
    }
  }, [showRegenerateModal, agreement?.property_designation]);

  // Fetch seller ID from conversation if needed
  useEffect(() => {
    if (conversationId) {
      const fetchConversationDetails = async () => {
        try {
          const response = await axios.get(`/api/conversations/${conversationId}`, {
            withCredentials: true
          });
          if (response.data && response.data.seller_id) {
            setSellerId(response.data.seller_id);
          }
        } catch (error) {
          console.error("Error fetching seller ID from conversation:", error);
        }
      };
      fetchConversationDetails();
    }
  }, [conversationId]);

  // Fetch broker agreement on component mount and when conversationId changes
  useEffect(() => {
    if (conversationId) {
      console.log('Component mounted or conversationId changed, fetching agreement...');
      fetchAgreement();
      fetchBrokerDescription();
    }
  }, [conversationId]);
  
  // The page view is now the default without toggle
  
  // New state for BankID signing process
  const [bankIdStatus, setBankIdStatus] = useState(null);
  const [orderRef, setOrderRef] = useState(null);
  
  // Konfigurera DOMPurify för att behålla radbrytningar och formatering
  const purifyConfig = {
    ADD_TAGS: ['br'],
    ADD_ATTR: ['style'],
    FORBID_TAGS: ['script', 'iframe', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick']
  };

  // Retrieve userType from localStorage
  const userType = localStorage.getItem('userType'); // 'seller' or 'broker'

  // Create a ref for the printable content
  const printRef = useRef();
  
  // Configure react-to-print with improved handling
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Mäklaravtal_${conversationId}`,
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: A4;
        margin: 1.5cm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
    onBeforeGetContent: () => {
      setIsPrinting(true);
      // Remove max-height constraint before printing
      if (printRef.current) {
        const content = printRef.current.querySelector('.agreement-content');
        if (content) {
          content.style.maxHeight = 'none';
          content.style.overflow = 'visible';
        }
      }
      return Promise.resolve();
    },
    onAfterPrint: () => {
      // Restore max-height constraint after printing
      if (printRef.current) {
        const content = printRef.current.querySelector('.agreement-content');
        if (content) {
          content.style.maxHeight = '842px';
          content.style.overflowY = 'auto';
        }
      }
      setIsPrinting(false);
    }
  });

  const [canAccessContactInfo, setCanAccessContactInfo] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [isTabActive, setIsTabActive] = useState(true); // Track if tab is active

  // Track tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabActive(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Add new state for scroll handling
  const [isFooterVisible, setIsFooterVisible] = useState(true);
  const footerRef = useRef(null);

  // Add scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (isMobile) { // Only apply on mobile
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const threshold = 100; // pixels from bottom

        // Hide footer when near bottom of page
        setIsFooterVisible(scrollPosition < documentHeight - threshold);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]); // Add isMobile as dependency

  // Helper function to validate agreement data
  const isValidAgreement = (agreementData) => {
    if (!agreementData || typeof agreementData !== 'object') {
      console.warn('Agreement data is not an object:', agreementData);
      return false;
    }
    
    if (!agreementData.id || !agreementData.conversation_id || !agreementData.content) {
      console.warn('Agreement data missing required fields:', {
        id: agreementData.id,
        conversation_id: agreementData.conversation_id,
        content: agreementData.content ? 'present' : 'missing'
      });
      return false;
    }
    
    return true;
  };

  // Function to normalize content through ReactQuill for consistent formatting
  const normalizeContentThroughQuill = (content) => {
    if (!content || typeof content !== 'string') {
      return '';
    }

    // Clean the content first
    const cleanedContent = cleanHtmlForEditor(content);
    
    // If we have access to the normalization Quill instance, use it
    if (normalizationQuillRef.current && normalizationQuillRef.current.getEditor) {
      try {
        const quillInstance = normalizationQuillRef.current.getEditor();
        quillInstance.clipboard.dangerouslyPasteHTML(cleanedContent);
        const normalizedContent = quillInstance.root.innerHTML;
        
        // Clear the content to avoid any visual artifacts
        quillInstance.setText('');
        
        return normalizedContent;
      } catch (error) {
        console.warn('Failed to normalize content through Quill instance, using cleaned version:', error);
      }
    }
    
    // Fallback to just cleaned content if ReactQuill processing fails
    return cleanedContent;
  };

  // Function to fetch the broker agreement
  const fetchAgreement = async () => {
    setLoading(true);
    try {
      console.log(`Fetching agreement for conversation ${conversationId}`);
      
      const response = await axios.get(
        `/api/conversations/${conversationId}/broker-agreement`,
        { withCredentials: true }
      );
      
      console.log('Fetch agreement response:', response.data);
      
      if (response.data && isValidAgreement(response.data)) {
        // Apply content normalization to ensure consistent appearance with ReactQuill editor
        const normalizedAgreement = {
          ...response.data,
          content: normalizeContentThroughQuill(response.data.content || '')
        };
        setAgreement(normalizedAgreement);
        if (response.data.property_designation) {
          setPropertyDesignation(response.data.property_designation);
        }
        console.log('Agreement fetched and set successfully');
      } else {
        console.warn('Received invalid agreement data:', response.data);
        setAgreement(null);
      }
      
    } catch (error) {
      console.log('Fetch agreement error:', error);
      
      if (error.response && error.response.status === 404) {
        // No agreement found or agreement is just a placeholder (PENDING_GENERATION);
        // user can generate one
        console.log('No agreement found (404) - user can generate one');
        setAgreement(null);
      } else {
        console.error('Error fetching agreement:', error);
        setError('Kunde inte hämta mäklaravtalet. Försök ladda om sidan.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to regenerate broker agreement using latest conversation history
  const handleRegenerate = async () => {
    // Close the modal first
    setShowRegenerateModal(false);
    
    // For brokers, check permissions first
    if (userType === 'broker') {
      // Recheck permissions to make sure we have the latest state
      await checkContactInfoAccess();
      
      if (!canAccessContactInfo) {
        setError('Du har inte tillgång till säljarens kontaktinformation. För att uppdatera mäklaravtalet, be säljaren att aktivera åtkomst till kontaktinformation.');
        return;
      }
      
      // Validate property designation
      if (!modalPropertyDesignation.trim()) {
        setError('Fastighetsbeteckning är obligatorisk.');
        return;
      }
    }

    setIsGenerating(true); // Set generating state to true
    try {
      // If we don't have sellerData in state, try to fetch it with the conversation_id
      let currentSellerData = sellerData;
      if (!currentSellerData && sellerId) {
        try {
          const sellerDataResponse = await axios.get(`/api/seller-data/by-seller/${sellerId}`, {
            params: {
              conversation_id: conversationId // Include conversation ID to get the correct seller data
            },
            withCredentials: true
          });
          if (sellerDataResponse.data) {
            currentSellerData = sellerDataResponse.data;
            
          }
        } catch (sellerDataError) {
          
        }
      } else {
        
      }
      
      // Include seller data in the regeneration request if available
      const payload = {
        ...(currentSellerData ? { seller_data: currentSellerData } : {}),
        property_designation: modalPropertyDesignation
      };
      
      console.log('Regenerating broker agreement with payload:', payload);
      
      const response = await axios.post(
        `/api/conversations/${conversationId}/broker-agreement/regenerate`,
        payload,
        { withCredentials: true }
      );
      
      console.log('Agreement regeneration response:', response.data);
      
      // Ensure we have a valid response before proceeding
      if (response.data && isValidAgreement(response.data)) {
        // Apply content normalization and set the agreement immediately from the response
        const normalizedAgreement = {
          ...response.data,
          content: normalizeContentThroughQuill(response.data.content || '')
        };
        setAgreement(normalizedAgreement);
        
        // Also update property designation if it came back from server
        if (response.data.property_designation) {
          setPropertyDesignation(response.data.property_designation);
        }
        
        console.log('Agreement regenerated and set successfully:', response.data);
        
        // Fetch the agreement again to ensure consistency (with a small delay)
        setTimeout(() => {
          fetchAgreement();
        }, 500);
        
        setError('');
        setModalPropertyDesignation(''); // Clear modal property designation after successful regeneration
      } else {
        console.error('Invalid response from server during regeneration:', response.data);
        setError('Mäklaravtalet uppdaterades men kunde inte visas. Ladda om sidan.');
      }
    } catch (error) {
      console.error('Error regenerating agreement:', error);
      setError('Kunde inte regenerera mäklaravtalet.');
    } finally {
      setIsGenerating(false); // Set generating state to false when done
    }
  };

  // Function to verify agreement hash
  const verifyAgreementHash = async () => {
    try {
      const response = await axios.get(
        `/api/conversations/${conversationId}/broker-agreement/verify`,
        { withCredentials: true }
      );
      setVerificationStatus(response.data);
      
    } catch (error) {
      console.error('Error verifying agreement hash:', error);
      setVerificationStatus({
        verified: false,
        message: 'Kunde inte verifiera avtalets integritet.'
      });
    }
  };

  // Function to fetch the broker's description and id
  const fetchBrokerDescription = async () => {
    try {
      const response = await axios.get(
        `/api/conversations/${conversationId}/broker-description`,
        { withCredentials: true }
      );
      setBrokerDescription(response.data.description);
      // Assuming the response includes the broker's id as 'id'
      if (response.data.id) {
        setBrokerId(response.data.id);
      }
      
    } catch (error) {
      console.error('Error fetching broker description:', error);
    }
  };

  // Function to fetch seller info when sellerId is available
  useEffect(() => {
    const fetchSellerInfo = async () => {
      if (sellerId) {
        try {
          const response = await axios.get(`${config.backend_url}/api/seller/${sellerId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            withCredentials: true
          });
          setSellerInfo(response.data);
        } catch (error) {
          console.error('Error fetching seller info:', error);
        }
      }
    };
    
    fetchSellerInfo();
  }, [sellerId]);

  // Fetch seller data (for address and contact info) when sellerId is available
  useEffect(() => {
    const fetchSellerData = async () => {
      if (sellerId) {
        try {
          // Use the correct endpoint that works with permissions, and include conversation_id
          const response = await axios.get(`${config.backend_url}/api/seller-data/by-seller/${sellerId}`, {
            params: {
              conversation_id: conversationId // Pass the conversation ID to get the correct seller data
            },
            withCredentials: true
          });
          if (response.data) {
            setSellerData(response.data);
            
          }
        } catch (error) {
          console.error('Error fetching seller data:', error);
        }
      }
    };
    
    fetchSellerData();
  }, [sellerId, conversationId]); // Add conversationId as dependency

  // Function to check if the broker has access to contact info
  const checkContactInfoAccess = async () => {
    
    
    
    if (!userIsLoggedIn || userType !== 'broker') {
      
      setIsCheckingPermissions(false);
      return;
    }

    setLoading(true);
    try {
      let conversationPermission = false;
      let globalPermission = false;
      
      // Check conversation-specific permission
      try {
        const conversationResponse = await axios.get(`${backend_url}/api/conversations/${conversationId}`, {
          withCredentials: true
        });
        
        conversationPermission = Boolean(conversationResponse?.data?.display_contact_info);
        
      } catch (convErr) {
        console.error('[Permission Check] Error checking conversation permission:', convErr);
      }
      
      // Check global permission as fallback
      if (sellerId) {
        try {
          const sellerResponse = await axios.get(`${backend_url}/api/sellers/${sellerId}/contact-info-status`, {
            withCredentials: true
          });
          
          globalPermission = Boolean(sellerResponse?.data?.display_contact_info);
          
        } catch (globalErr) {
          console.error('[Permission Check] Error checking global permission:', globalErr);
        }
      } else {
        
      }
      
      const hasAccess = conversationPermission || globalPermission;
      
      
      setCanAccessContactInfo(hasAccess);
    } catch (error) {
      console.error('[Permission Check] Error in main permission check:', error);
      setCanAccessContactInfo(false);
    } finally {
      setLoading(false);
      setIsCheckingPermissions(false);
    }
  };

  // Set up an interval to check permissions every 30 seconds when user is a broker
  useEffect(() => {
    if (userType === 'broker' && userIsLoggedIn) {
      
      
      // Run initial check
      checkContactInfoAccess();
      
      const intervalId = setInterval(() => {
        if (document.hidden || isEditing) {
          
          return;
        }
        
        
        checkContactInfoAccess();
      }, 30000); // Check every 30 seconds
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [userType, userIsLoggedIn, conversationId, sellerId, isEditing]);

  const handleGenerate = async () => {
    if (!propertyDesignation.trim()) {
      setError('Fastighetsbeteckning är obligatorisk.');
      return;
    }
    
    setError('');
    setIsGenerating(true);
    
    try {
      // If we don't have sellerData in state, try to fetch it with the conversation_id
      let currentSellerData = sellerData;
      if (!currentSellerData && sellerId) {
        try {
          const sellerDataResponse = await axios.get(`/api/seller-data/by-seller/${sellerId}`, {
            params: {
              conversation_id: conversationId
            },
            withCredentials: true
          });
          if (sellerDataResponse.data) {
            currentSellerData = sellerDataResponse.data;
            console.log('Fetched seller data for agreement generation:', currentSellerData);
          }
        } catch (sellerDataError) {
          console.warn('Could not fetch seller data:', sellerDataError);
        }
      }
      
      // Include seller data in the generation request if available
      const payload = {
        ...(currentSellerData ? { seller_data: currentSellerData } : {}),
        property_designation: propertyDesignation
      };
      
      console.log('Generating broker agreement with payload:', payload);
      
      const response = await axios.post(
        `/api/conversations/${conversationId}/broker-agreement`,
        payload,
        { withCredentials: true }
      );
      
      console.log('Agreement generation response:', response.data);
      
      // Ensure we have a valid response before proceeding
      if (response.data && isValidAgreement(response.data)) {
        // Set the agreement immediately from the response
        const normalizedAgreement = {
          ...response.data,
          content: normalizeContentThroughQuill(response.data.content || '')
        };
        setAgreement(normalizedAgreement);
        
        // Also update property designation if it came back from server
        if (response.data.property_designation) {
          setPropertyDesignation(response.data.property_designation);
        }
        
        console.log('Agreement set successfully:', response.data);
        
        // Fetch the agreement again to ensure consistency (with a small delay)
        setTimeout(() => {
          fetchAgreement();
        }, 500);
        
        setError('');
      } else {
        console.error('Invalid response from server:', response.data);
        setError('Mäklaravtalet genererades men kunde inte visas. Ladda om sidan.');
      }
      
    } catch (error) {
      console.error('Error generating agreement:', error);
      
      // Check if it's a specific error about minimal agreement
      if (error.response && error.response.data && error.response.data.error) {
        if (error.response.data.error.includes('Missing conversation ID or property designation')) {
          setError('Konversations-ID och fastighetsbeteckning krävs för att generera avtalet.');
        } else if (error.response.data.error.includes('No seller data found')) {
          // Try to generate a minimal agreement
          try {
            const minimalPayload = {
              property_designation: propertyDesignation
            };
            
            console.log('Trying minimal agreement generation with payload:', minimalPayload);
            
            const minimalResponse = await axios.post(
              `/api/conversations/${conversationId}/broker-agreement`,
              minimalPayload,
              { withCredentials: true }
            );
            
            console.log('Minimal agreement generation response:', minimalResponse.data);
            
            if (minimalResponse.data && minimalResponse.data.id) {
              // Apply content normalization for minimal agreement too
              const normalizedMinimalAgreement = {
                ...minimalResponse.data,
                content: normalizeContentThroughQuill(minimalResponse.data.content || '')
              };
              setAgreement(normalizedMinimalAgreement);
              if (minimalResponse.data.property_designation) {
                setPropertyDesignation(minimalResponse.data.property_designation);
              }
              setError('');
              
              // Fetch again to ensure consistency
              setTimeout(() => {
                fetchAgreement();
              }, 500);
            } else {
              setError('Kunde inte generera mäklaravtalet.');
            }
            
          } catch (minimalError) {
            console.error('Error generating minimal agreement:', minimalError);
            setError('Kunde inte generera mäklaravtalet.');
          }
        } else {
          setError(`Kunde inte generera mäklaravtalet: ${error.response.data.error}`);
        }
      } else {
        setError('Kunde inte generera mäklaravtalet. Kontrollera din internetanslutning och försök igen.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `/api/conversations/${conversationId}/broker-agreement`,
        { content: editedContent },
        { withCredentials: true }
      );
      
      // Update agreement with the response data which includes reset signatures
      if (response.data) {
        // Normalize the content for consistent display
        const normalizedAgreement = {
          ...response.data,
          content: normalizeContentThroughQuill(response.data.content || editedContent)
        };
        setAgreement(normalizedAgreement);
      } else {
        // Fallback: manually reset signatures to match backend behavior
        setAgreement(prev => ({ 
          ...prev, 
          content: normalizeContentThroughQuill(editedContent),
          seller_signed: false,
          broker_signed: false,
          is_fully_signed: false,
          content_hash: null
        }));
      }
      
      setIsEditing(false);
      setError('');
      setHasContentChanged(false); // Reset content changed flag
      
    } catch (error) {
      console.error('Error saving agreement:', error);
      setError('Kunde inte spara mäklaravtalet.');
    }
    
    // Send message to other party after saving
    try {
      const userType = localStorage.getItem('userType');
      const userId = localStorage.getItem('userId');
      
      let messagePayload = {
        content: `${userType === 'broker' ? 'Mäklaren' : 'Säljaren'} har uppdaterat mäklaravtalet. Kontrollera att avtalet stämmer överens med dina önskemål`
      };
      
      await axios.post(
        `/api/conversations/${conversationId}/messages`,
        messagePayload,
        { withCredentials: true }
      );
      
      
    } catch (notificationError) {
      console.error('Error sending agreement change notification:', notificationError);
      // Don't set error for notification failure as the main action succeeded
    }
  };

  const handleSign = async () => {
    const userType = localStorage.getItem('userType');
    
    // Set initial modal state
    setBankIdStatus('initiating');
    setError('');
    
    if (userType === 'broker') {
      // Broker signing logic
      try {
        const response = await axios.post(
          `/api/conversations/${conversationId}/broker-agreement/sign-broker`,
          {},
          { withCredentials: true }
        );
        
        if (response.data.orderRef) {
          // BankID authentication started
          const newOrderRef = response.data.orderRef;
          setOrderRef(newOrderRef);
          setBankIdStatus('pending');
          
          localStorage.setItem('bankid_orderRef', newOrderRef);
          localStorage.setItem('bankid_timestamp', Date.now().toString());
          
          // Start polling for completion
          let pollCount = 0;
          const maxPolls = 120; // 2 minutes with 1-second intervals
          
          const pollInterval = setInterval(async () => {
            pollCount++;
            
            try {
              const collectResponse = await axios.post(
                `${backend_url}/bankid/collect`,
                { order_ref: newOrderRef },
                { withCredentials: true }
              );
              
              if (collectResponse.data.status === 'complete') {
                clearInterval(pollInterval);
                localStorage.removeItem('bankid_orderRef');
                localStorage.removeItem('bankid_timestamp');
                
                setBankIdStatus('complete');
                
                // Send message notification for broker signing
                try {
                  await axios.post(
                    `/api/conversations/${conversationId}/messages`,
                    { content: 'Mäklaren har signerat mäklaravtalet.' },
                    { withCredentials: true }
                  );
                } catch (notificationError) {
                  console.error('Error sending broker signing notification:', notificationError);
                }
                
                // Hide modal after a moment and refresh agreement
                setTimeout(() => {
                  setBankIdStatus(null);
                  setOrderRef(null);
                  fetchAgreement();
                }, 2000);
                
              } else if (collectResponse.data.status === 'failed') {
                clearInterval(pollInterval);
                localStorage.removeItem('bankid_orderRef');
                localStorage.removeItem('bankid_timestamp');
                setBankIdStatus('failed');
                setOrderRef(null);
              }
            } catch (pollError) {
              if (pollCount >= maxPolls) {
                clearInterval(pollInterval);
                localStorage.removeItem('bankid_orderRef');
                localStorage.removeItem('bankid_timestamp');
                setBankIdStatus('expired');
                setOrderRef(null);
              }
              // Continue polling for other errors
            }
          }, 1000);
          
        } else {
          setBankIdStatus('error');
          setOrderRef(null);
        }
      } catch (error) {
        console.error('Error initiating BankID sign for broker:', error);
        setBankIdStatus('error');
        setOrderRef(null);
      }
    } else {
      // Seller signing logic
      try {
        const response = await axios.post(
          `/api/conversations/${conversationId}/broker-agreement/sign-seller`,
          {},
          { withCredentials: true }
        );
        
        if (response.data.orderRef) {
          // BankID authentication started
          const newOrderRef = response.data.orderRef;
          setOrderRef(newOrderRef);
          setBankIdStatus('pending');
          
          localStorage.setItem('bankid_orderRef', newOrderRef);
          localStorage.setItem('bankid_timestamp', Date.now().toString());
          
          // Start polling for completion
          let pollCount = 0;
          const maxPolls = 120; // 2 minutes with 1-second intervals
          
          const pollInterval = setInterval(async () => {
            pollCount++;
            
            try {
              const collectResponse = await axios.post(
                `${backend_url}/bankid/collect`,
                { order_ref: newOrderRef },
                { withCredentials: true }
              );
              
              if (collectResponse.data.status === 'complete') {
                clearInterval(pollInterval);
                localStorage.removeItem('bankid_orderRef');
                localStorage.removeItem('bankid_timestamp');
                
                setBankIdStatus('complete');
                
                // Send message notification for seller signing
                try {
                  await axios.post(
                    `/api/conversations/${conversationId}/messages`,
                    { content: 'Säljaren har signerat mäklaravtalet.' },
                    { withCredentials: true }
                  );
                } catch (notificationError) {
                  console.error('Error sending seller signing notification:', notificationError);
                }
                
                // Hide modal after a moment and refresh agreement
                setTimeout(() => {
                  setBankIdStatus(null);
                  setOrderRef(null);
                  fetchAgreement();
                }, 2000);
                
              } else if (collectResponse.data.status === 'failed') {
                clearInterval(pollInterval);
                localStorage.removeItem('bankid_orderRef');
                localStorage.removeItem('bankid_timestamp');
                setBankIdStatus('failed');
                setOrderRef(null);
              }
            } catch (pollError) {
              if (pollCount >= maxPolls) {
                clearInterval(pollInterval);
                localStorage.removeItem('bankid_orderRef');
                localStorage.removeItem('bankid_timestamp');
                setBankIdStatus('expired');
                setOrderRef(null);
              }
              // Continue polling for other errors
            }
          }, 1000);
          
        } else {
          setBankIdStatus('error');
          setOrderRef(null);
        }
      } catch (error) {
        console.error('Error initiating BankID sign:', error);
        setBankIdStatus('error');
        setOrderRef(null);
      }
    }
  };

  // Function to clean HTML: removes empty paragraphs with only <br> and normalizes multiple <br> tags.
  const cleanHtmlForEditor = (html) => {
    if (!html) return '';
    
    let cleaned = html;
    
    // Remove empty paragraphs with only <br> tags
    cleaned = cleaned.replace(/<p>\s*<br\s*\/?>\s*<\/p>/g, '');
    
    // Remove empty paragraphs with only whitespace
    cleaned = cleaned.replace(/<p>\s*<\/p>/g, '');
    
    // Normalize multiple <br> tags to single <br>
    cleaned = cleaned.replace(/(<br\s*\/?>\s*)+/g, '<br>');
    
    // Remove <br> tags at the beginning of paragraphs
    cleaned = cleaned.replace(/<p>\s*<br\s*\/?>\s*/g, '<p>');
    
    // Remove <br> tags at the end of paragraphs
    cleaned = cleaned.replace(/\s*<br\s*\/?>\s*<\/p>/g, '</p>');
    
    // Clean up spacing around headers (h1, h2, h3, h4, h5, h6)
    // Remove empty paragraphs before headers
    cleaned = cleaned.replace(/<p>\s*<\/p>\s*(<h[1-6][^>]*>)/g, '$1');
    
    // Remove empty paragraphs after headers
    cleaned = cleaned.replace(/(<\/h[1-6]>)\s*<p>\s*<\/p>/g, '$1');
    
    // Remove <br> tags immediately before headers
    cleaned = cleaned.replace(/<br\s*\/?>\s*(<h[1-6][^>]*>)/g, '$1');
    
    // Remove <br> tags immediately after headers
    cleaned = cleaned.replace(/(<\/h[1-6]>)\s*<br\s*\/?>/g, '$1');
    
    // Normalize spacing between headers and following content
    cleaned = cleaned.replace(/(<\/h[1-6]>)\s*(<p>)/g, '$1$2');
    
    // Remove multiple consecutive empty lines (multiple <p></p> or <br> sequences)
    cleaned = cleaned.replace(/(<p>\s*<\/p>\s*){2,}/g, '');
    cleaned = cleaned.replace(/(<br\s*\/?>\s*){3,}/g, '<br><br>');
    
    // Clean up spacing around lists
    cleaned = cleaned.replace(/<p>\s*<\/p>\s*(<[uo]l>)/g, '$1');
    cleaned = cleaned.replace(/(<\/[uo]l>)\s*<p>\s*<\/p>/g, '$1');
    
    // Remove trailing <br> tags at the end of content
    cleaned = cleaned.replace(/(<br\s*\/?>\s*)+$/, '');
    
    // Remove leading <br> tags at the beginning of content
    cleaned = cleaned.replace(/^(\s*<br\s*\/?>\s*)+/, '');
    
    return cleaned.trim();
  };

  // Function to handle content changes in the editor
  const handleContentChange = (content) => {
    setEditedContent(content);
    // Compare with original content to determine if there's a change
    const originalContent = agreement.content || '';
    setHasContentChanged(content !== originalContent);
  };

  // Function to initiate editing the agreement
  const handleEdit = () => {
    // Store current scroll position before switching to edit mode
    scrollPositionRef.current = window.scrollY;
    
    setEditedContent(agreement.content);
    setIsEditing(true);
    setError('');
    setHasContentChanged(false); // Reset the change tracker
    
    // Set flag to preserve scroll position after rendering editor
    setPreserveScrollAfterChange(true);
  };

  // Function to cancel editing and reset to the original agreement content
  const handleCancelEdit = () => {
    // Store current scroll position before canceling edit
    scrollPositionRef.current = window.scrollY;
    
    setEditedContent(agreement.content || '');
    setIsEditing(false);
    setError('');
    setHasContentChanged(false); // Reset the change tracker
    
    // Set flag to preserve scroll position after rendering view mode
    setPreserveScrollAfterChange(true);
  };

  // Render contact info permission message for brokers
  const renderContactInfoMessage = () => {
    if (userType === 'broker') {
      if (!isCheckingPermissions && canAccessContactInfo) {
        // Similar contact box style as in SellerDataDetail.js
        // Still show the box even if some data is missing
        return (
          <div className="contact-info-card" style={{
            background: 'var(--white, #fff)',
            borderRadius: '10px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            border: '1px solid var(--duck-blue-7, #e2e8f0)',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ 
              marginTop: '0', 
              marginBottom: '1.5rem', 
              color: 'var(--blue-1, #2c3e50)',
              borderBottom: '1px solid var(--duck-blue-7, #e2e8f0)',
              paddingBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              <FontAwesomeIcon icon={faHome} style={{ marginRight: '10px', color: 'var(--blue-3, #3498db)' }} />
              Kontaktuppgifter
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              {sellerInfo && sellerInfo.firstname && sellerInfo.lastname && (
                <p style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0.7rem 0',
                  color: 'var(--grey-2, #7f8c8d)'
                }}>
                  <FontAwesomeIcon icon={faUser} style={{ 
                    marginRight: '10px', 
                    color: 'var(--blue-3, #3498db)', 
                    width: '20px' 
                  }} />
                  <span style={{ fontWeight: 'bold', color: 'var(--grey-1, #2c3e50)', marginRight: '8px' }}>Namn:</span> 
                  {sellerInfo.firstname} {sellerInfo.lastname}
                </p>
              )}
              
              {sellerData && sellerData.address && (
                <p style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0.7rem 0',
                  color: 'var(--grey-2, #7f8c8d)'
                }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ 
                    marginRight: '10px', 
                    color: 'var(--blue-3, #3498db)', 
                    width: '20px' 
                  }} />
                  <span style={{ fontWeight: 'bold', color: 'var(--grey-1, #2c3e50)', marginRight: '8px' }}>Adress:</span> 
                  {sellerData.address}
                </p>
              )}
              
              <p style={{ 
                display: 'flex', 
                alignItems: 'center', 
                margin: '0.7rem 0',
                color: 'var(--grey-2, #7f8c8d)'
              }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ 
                  marginRight: '10px', 
                  color: 'var(--blue-3, #3498db)', 
                  width: '20px' 
                }} />
                <span style={{ fontWeight: 'bold', color: 'var(--grey-1, #2c3e50)', marginRight: '8px' }}>E-post:</span> 
                {(sellerInfo && sellerInfo.email) || 'Ej angiven'}
              </p>
              
              {sellerInfo && sellerInfo.phone && (
                <p style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0.7rem 0',
                  color: 'var(--grey-2, #7f8c8d)'
                }}>
                  <FontAwesomeIcon icon={faPhone} style={{ 
                    marginRight: '10px', 
                    color: 'var(--blue-3, #3498db)', 
                    width: '20px' 
                  }} />
                  <span style={{ fontWeight: 'bold', color: 'var(--grey-1, #2c3e50)', marginRight: '8px' }}>Telefon:</span> 
                  {sellerInfo.phone}
                </p>
              )}
            </div>
          </div>
        );
      } else if (!isCheckingPermissions && !canAccessContactInfo) {
        return (
          <div className="contact-info-message">
            <div className="info-icon">ℹ️</div>
            <div className="message-content">
              <h3>Kontaktinformation krävs</h3>
              <p>För att generera ett mäklaravtal behöver du tillgång till säljarens kontaktinformation.</p>
              <p>Be säljaren att aktivera åtkomst till kontaktinformation för den här konversationen.</p>
            </div>
          </div>
        );
      }
    }
    return null;
  };


  // Add effect to restore scroll position after render
  useEffect(() => {
    if (preserveScrollAfterChange) {
      // Restore scroll position after component updates
      window.scrollTo(0, scrollPositionRef.current);
      // Reset the flag
      setPreserveScrollAfterChange(false);
    }
  }, [isEditing, agreement, preserveScrollAfterChange]);

  return (
    <>
      <style>{styles}</style>
      <div className="broker-agreement-container" style={{ 
        border: '1px solid #ccc', 
        padding: '2rem', 
        marginTop: '1rem',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#fff',
        position: 'relative', // Add position relative for spinner overlay
        maxWidth: '1200px', // Increased from default
        width: '100%', // Säkerställer att bredd är 100%
        boxSizing: 'border-box', // Inkluderar padding i bredden
        overflowX: 'hidden' // Förhindrar horisontell scrollning
      }}>
        {/* Show regenerate confirmation modal when showRegenerateModal is true */}
        {showRegenerateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Bekräfta uppdatering</h3>
              
              <p style={{ marginBottom: '1.5rem', lineHeight: '1.5' }}>
                Är du säker på att du vill ersätta det befintliga avtalet med ett uppdaterat avtal som tar hänsyn till senaste chatthistoriken? Du kan inte ångra dig senare.
              </p>
              
              {/* Property designation input field in modal */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="modalPropertyDesignation" style={{ 
                  display: 'block', 
                  textAlign: 'left', 
                  marginBottom: '0.5rem',
                  fontWeight: '500' 
                }}>
                  Fastighetsbeteckning <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="modalPropertyDesignation"
                  type="text"
                  value={modalPropertyDesignation}
                  onChange={(e) => setModalPropertyDesignation(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Ange fastighetsbeteckning"
                />
                <small style={{ display: 'block', textAlign: 'left', color: '#666', marginTop: '0.3rem' }}>
                  Ex: Karlsborg 2:14, Stockholm Bostadsrätt 123
                </small>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  onClick={() => {
                    setShowRegenerateModal(false);
                    setModalPropertyDesignation('');
                  }} 
                  style={{ 
                    backgroundColor: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}>
                  Avbryt
                </button>
                <button 
                  onClick={handleRegenerate}
                  disabled={!modalPropertyDesignation.trim()}
                  style={{ 
                    backgroundColor: modalPropertyDesignation.trim() ? '#2980b9' : '#b2c8d8',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: modalPropertyDesignation.trim() ? 'pointer' : 'not-allowed',
                    fontWeight: '500'
                  }}>
                  Ja, uppdatera
                </button>
              </div>
              {isGenerating && <SpinnerOverlay />}
            </div>
          </div>
        )}
        
        {/* BankID Status Modal */}
        {bankIdStatus && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              position: 'relative'
            }}>
              {/* Close button */}
              {['failed', 'error', 'expired'].includes(bankIdStatus) && (
                <button 
                  onClick={() => setBankIdStatus(null)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    color: '#777'
                  }}
                >
                  ✕
                </button>
              )}
              
              {/* Status icon */}
              <div style={{ marginBottom: '1.5rem' }}>
                {bankIdStatus === 'initiating' && (
                  <div style={{ 
                    width: '60px', 
                    height: '60px', 
                    borderRadius: '50%', 
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #3498db', 
                    margin: '0 auto',
                    animation: 'spin 1s linear infinite',
                  }} />
                )}
                {bankIdStatus === 'pending' && (
                  <div style={{ 
                    backgroundColor: '#3498db',
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}>
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="white">
                      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" />
                    </svg>
                  </div>
                )}
                {bankIdStatus === 'complete' && (
                  <div style={{ 
                    backgroundColor: '#2ecc71',
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}>
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="white">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  </div>
                )}
                {['failed', 'error', 'expired'].includes(bankIdStatus) && (
                  <div style={{ 
                    backgroundColor: '#e74c3c',
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}>
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="white">
                      <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Status heading */}
              <h3 style={{ color: '#333', marginBottom: '1rem' }}>
                {bankIdStatus === 'initiating' && 'Initierar BankID'}
                {bankIdStatus === 'pending' && 'Väntar på BankID'}
                {bankIdStatus === 'complete' && 'Signering slutförd!'}
                {bankIdStatus === 'failed' && 'Signering misslyckades'}
                {bankIdStatus === 'error' && 'Ett fel uppstod'}
                {bankIdStatus === 'expired' && 'Signeringen har avbrutits'}
              </h3>
              
              {/* Status message */}
              <p style={{ color: '#666', lineHeight: '1.5', marginBottom: '1.5rem' }}>
                {bankIdStatus === 'initiating' && 'Förbereder signering med BankID...'}
                {bankIdStatus === 'pending' && (
                  <>
                    <strong>Öppna Mobilt BankID på din enhet</strong><br />
                    Följ instruktionerna i BankID-appen för att slutföra signeringen.
                  </>
                )}
                {bankIdStatus === 'complete' && 'Avtalet har signerats framgångsrikt med Mobilt BankID.'}
                {bankIdStatus === 'failed' && 'BankID-signeringen kunde inte slutföras. Kontrollera att du har rätt BankID installerat och försök igen.'}
                {bankIdStatus === 'error' && 'Ett tekniskt fel uppstod vid kommunikation med BankID-tjänsten. Försök igen senare.'}
                {bankIdStatus === 'expired' && 'Signeringen tog för lång tid att slutföra eller avbröts. Starta om signeringsprocessen.'}
              </p>
              
              {/* Action buttons */}
              {['failed', 'error', 'expired'].includes(bankIdStatus) && (
                <button
                  className="agreement-button agreement-button-primary"
                  onClick={() => handleSign()}
                >
                  Försök igen
                </button>
              )}
              
              {/* Order reference */}
              {bankIdStatus === 'pending' && orderRef && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '10px', 
                  backgroundColor: '#f9f9f9', 
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  color: '#666' 
                }}>
                  OrderRef: {orderRef?.substring(0, 8)}...
                </div>
              )}
            </div>
          </div>
        )}
      
        <h2 style={{ 
          borderBottom: '2px solid #2c3e50', 
          paddingBottom: '10px', 
          color: '#2c3e50',
          fontWeight: '600',
          fontSize: 'clamp(1.2rem, 5vw, 1.5rem)', // Responsiv textstorlek
          wordWrap: 'break-word', // Bryt långa ord
          overflowWrap: 'break-word', // Säkerställer att text bryts vid behov
          hyphens: 'auto' // Aktiverar avstavning för långa ord
        }}>Mäklaravtal (Förmedlingsuppdrag)</h2>

        {error && <p style={{ color: 'red', fontWeight: 'bold', backgroundColor: '#ffe6e6', padding: '10px', borderRadius: '4px' }}>{error}</p>}

        {/* Display Document Verification Status */}
        {verificationStatus && (
          <div className="mobile-card">
            <div className="mobile-card-content" style={{ 
              backgroundColor: verificationStatus.verified ? '#e8f5e9' : '#ffebee',
              borderLeft: `4px solid ${verificationStatus.verified ? '#4caf50' : '#f44336'}`
            }}>
              <p style={{ 
                margin: '0', 
                fontWeight: 'bold', 
                color: verificationStatus.verified ? '#2e7d32' : '#c62828' 
              }}>
                <span style={{ marginRight: '6px' }}>{verificationStatus.verified ? '✓' : '⚠️'}</span>
                {verificationStatus.message}
              </p>
            </div>
          </div>
        )}

        {/* Display Broker's Description */}
        {userType !== 'broker' && brokerDescription ? (
          <div className="mobile-card">
            <div className="mobile-card-header">Mäklare Information</div>
            <div className="mobile-card-content">
              <p style={{ margin: '0', lineHeight: '1.5' }}>{brokerDescription}</p>
            </div>
          </div>
        ) : userType !== 'broker' && (
          <div className="mobile-card">
            <div className="mobile-card-header">Mäklare Information</div>
            <div className="mobile-card-content">
              <p style={{ margin: '0', fontStyle: 'italic' }}>Ingen beskrivning tillgänglig.</p>
            </div>
          </div>
        )}

        {/* Display contact info message for brokers */}
        {renderContactInfoMessage()}

        {/* Agreement display logic */}
        {!agreement ? (
          <div className="mobile-card">
            <div className="mobile-card-content" style={{ textAlign: 'center', position: 'relative' }}>
              <p style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Inget mäklaravtal har genererats ännu.</p>
              {/* Show property designation input and generate button only for brokers with contact info permission */}
              {(userType === 'broker' && canAccessContactInfo) && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="propertyDesignation" style={{ 
                      display: 'block', 
                      textAlign: 'left', 
                      marginBottom: '0.5rem',
                      fontWeight: '500' 
                    }}>
                      Fastighetsbeteckning <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      id="propertyDesignation"
                      type="text"
                      value={propertyDesignation}
                      onChange={(e) => setPropertyDesignation(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Ange fastighetsbeteckning"
                    />
                    <small style={{ display: 'block', textAlign: 'left', color: '#666', marginTop: '0.3rem' }}>
                      Ex: Karlsborg 2:14, Stockholm Bostadsrätt 123
                    </small>
                  </div>
                  <button 
                    onClick={handleGenerate} 
                    className="agreement-button agreement-button-primary">
                    Generera Mäklaravtal
                  </button>
                </>
              )}
              {isGenerating && <SpinnerOverlay />}
            </div>
          </div>
        ) : (
          <div>
            <div className="mobile-card">
              <div className="mobile-card-header">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: agreement.is_fully_signed ? '#38c172' : '#fc8181',
                    boxShadow: '0 0 6px rgba(0,0,0,0.1)'
                  }}></div>
                  <p style={{ 
                    margin: '0', 
                    fontWeight: '600', 
                    color: agreement.is_fully_signed ? '#38c172' : '#4a5568',
                    fontSize: 'clamp(0.9rem, 4vw, 1rem)',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    letterSpacing: '0.01em'
                  }}>
                    Status: {agreement.is_fully_signed ? 'Avtalet är signerat.' : 'Avtalet är inte signerat ännu.'}
                  </p>
                </div>
              </div>
              
              <div className="mobile-card-content">
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backgroundColor: agreement.seller_signed ? 'rgba(56, 193, 114, 0.1)' : 'rgba(0, 0, 0, 0.03)',
                    padding: '12px',
                    borderRadius: '8px',
                    border: agreement.seller_signed ? '1px solid rgba(56, 193, 114, 0.2)' : '1px solid rgba(0, 0, 0, 0.06)'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: agreement.seller_signed ? '2px solid #38c172' : '2px solid #a0aec0',
                      borderRadius: '4px',
                      position: 'relative',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {agreement.seller_signed && (
                        <div style={{
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#38c172',
                          borderRadius: '2px'
                        }}></div>
                      )}
                    </div>
                    <span style={{ 
                      fontSize: 'clamp(0.8rem, 3.5vw, 0.9rem)',
                      color: agreement.seller_signed ? '#2f855a' : '#4a5568',
                      fontWeight: agreement.seller_signed ? '600' : '500'
                    }}>
                      Säljare {agreement.seller_signed ? 
                        <span style={{ color: '#38c172', fontWeight: '600', fontSize: 'clamp(0.8rem, 3.5vw, 0.9rem)' }}>(signerat)</span> : 
                        <span style={{ color: '#a0aec0', fontSize: 'clamp(0.8rem, 3.5vw, 0.9rem)' }}>(ej signerat)</span>}
                    </span>
                  </div>
                  
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backgroundColor: agreement.broker_signed ? 'rgba(56, 193, 114, 0.1)' : 'rgba(0, 0, 0, 0.03)',
                    padding: '12px',
                    borderRadius: '8px',
                    border: agreement.broker_signed ? '1px solid rgba(56, 193, 114, 0.2)' : '1px solid rgba(0, 0, 0, 0.06)'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: agreement.broker_signed ? '2px solid #38c172' : '2px solid #a0aec0',
                      borderRadius: '4px',
                      position: 'relative',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {agreement.broker_signed && (
                        <div style={{
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#38c172',
                          borderRadius: '2px'
                        }}></div>
                      )}
                    </div>
                    <span style={{ 
                      fontSize: 'clamp(0.8rem, 3.5vw, 0.9rem)',
                      color: agreement.broker_signed ? '#2f855a' : '#4a5568',
                      fontWeight: agreement.broker_signed ? '600' : '500'
                    }}>
                      Mäklare {agreement.broker_signed ? 
                        <span style={{ color: '#38c172', fontWeight: '600', fontSize: 'clamp(0.8rem, 3.5vw, 0.9rem)' }}>(signerat)</span> : 
                        <span style={{ color: '#a0aec0', fontSize: 'clamp(0.8rem, 3.5vw, 0.9rem)' }}>(ej signerat)</span>}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Show hash information if available */}
            {agreement.content_hash && (
              <div className="mobile-card">
                <div className="mobile-card-content" style={{
                  backgroundColor: '#f8f9fa',
                  fontSize: '0.8rem',
                  fontFamily: 'monospace'
                }}>
                  <p style={{ margin: '0', color: '#666' }}>
                    <strong>Dokumenthash:</strong> {agreement.content_hash}
                  </p>
                </div>
              </div>
            )}

            {isEditing ? (
              <div className="mobile-card">
                <div className="mobile-card-content">
                  <div className="quill-editor-container" style={{ 
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    backgroundColor: '#fcfcfc',
                    maxHeight: 'none',
                    position: 'relative'
                  }} ref={editorRef}>
                    <ReactQuill
                      theme="snow"
                      value={editedContent}
                      onChange={(content) => {
                        handleContentChange(content);
                        // Store scroll position when content changes
                        if (editorRef.current) {
                          scrollPositionRef.current = window.scrollY;
                        }
                      }}
                      style={{ 
                        height: 'auto',
                        minHeight: '50vh',
                        overflow: 'visible'
                      }}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline'],
                          [{'list': 'ordered'}, {'list': 'bullet'}],
                          ['clean']
                        ]
                      }}
                    />
                  </div>
                </div>

                {/* Add non-editable signature section for visual consistency */}
                <div className="mobile-card signature-section-preview" style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                  opacity: '0.8'
                }}>
                  <h3 style={{ marginTop: 0, borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>Underskrifter (förhandsvisning)</h3>

                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Seller signature line */}
                    <div>
                      <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Säljare:</p>
                      <div style={{ 
                        borderBottom: '1px solid #000', 
                        height: '30px',
                        position: 'relative',
                        marginBottom: '5px'
                      }}>
                        {agreement.seller_signed && (
                          <div style={{
                            position: 'absolute',
                            top: '5px',
                            left: '0',
                            right: '0',
                            textAlign: 'center',
                            color: '#27ae60',
                            fontWeight: 'bold'
                          }}>
                            Signerat med Mobilt BankID
                          </div>
                        )}
                      </div>
                      <p style={{ fontSize: '0.9rem', color: '#666', margin: '5px 0 0 0' }}>
                        Datum: {agreement.seller_signed ? new Date(agreement.updated_at).toLocaleDateString('sv-SE') : '________________'}
                      </p>
                    </div>
                    
                    {/* Broker signature line */}
                    <div>
                      <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Mäklare:</p>
                      <div style={{ 
                        borderBottom: '1px solid #000', 
                        height: '30px',
                        position: 'relative',
                        marginBottom: '5px'
                      }}>
                        {agreement.broker_signed && (
                          <div style={{
                            position: 'absolute',
                            top: '5px',
                            left: '0',
                            right: '0',
                            textAlign: 'center',
                            color: '#27ae60',
                            fontWeight: 'bold'
                          }}>
                            Signerat med Mobilt BankID
                          </div>
                        )}
                      </div>
                      <p style={{ fontSize: '0.9rem', color: '#666', margin: '5px 0 0 0' }}>
                        Datum: {agreement.broker_signed ? new Date(agreement.updated_at).toLocaleDateString('sv-SE') : '________________'}
                      </p>
                    </div>
                  </div>
                  
                  {agreement.content_hash && (
                    <div style={{
                      borderTop: '1px solid #ddd',
                      paddingTop: '15px',
                      marginTop: '20px',
                      fontSize: '0.8rem',
                      color: '#666'
                    }}>
                      <p style={{ margin: '0' }}>
                        <strong>Avtalets unika dokumenthash:</strong> {agreement.content_hash}
                      </p>
                      <p style={{ margin: '5px 0 0 0', fontStyle: 'italic' }}>
                        Detta avtal är digitalt signerat med BankID och lagras i krypterad form.
                      </p>
                    </div>
                  )}
                </div>

                {error && <p style={{ color: 'red', fontWeight: 'bold', backgroundColor: '#ffe6e6', padding: '10px', borderRadius: '4px', marginTop: '1rem' }}>{error}</p>}
                
                <div className="mobile-card-footer no-print">
                  <button 
                    onClick={handleCancelEdit} 
                    disabled={loading}
                    className="agreement-button agreement-button-gray">
                    Avbryt
                  </button>
                  {hasContentChanged && (
                    <button 
                      onClick={handleSave} 
                      disabled={loading}
                      className="agreement-button agreement-button-secondary">
                      {loading ? 'Sparar...' : 'Spara Ändringar'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                {/* Printable content - wrapped in a div with ref */}
                <div ref={printRef} style={{ pageBreakInside: 'avoid' }}>
                  <div
                    className="agreement-content mobile-card"
                    style={{
                      height: 'auto',
                      minHeight: '50vh',
                      maxHeight: 'none',
                      overflowY: 'visible',
                      backgroundColor: 'white'
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(agreement.content, purifyConfig) }}
                  />
                  
                  {/* Add signature lines and signing info at the bottom of the agreement */}
                  <div 
                    className="signature-section mobile-card">
                    <h3 style={{ marginTop: 0, borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>Underskrifter</h3>
                    
                    {/* Signing date when fully signed */}
                    {agreement.is_fully_signed && (
                      <div style={{ textAlign: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
                        Avtal signerat den: {new Date(agreement.updated_at).toLocaleDateString('sv-SE')}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Seller signature line */}
                      <div>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Säljare:</p>
                        <div style={{ 
                          borderBottom: '1px solid #000', 
                          height: '30px',
                          position: 'relative',
                          marginBottom: '5px'
                        }}>
                          {agreement.seller_signed && (
                            <div style={{
                              position: 'absolute',
                              top: '5px',
                              left: '0',
                              right: '0',
                              textAlign: 'center',
                              color: '#27ae60',
                              fontWeight: 'bold'
                            }}>
                              Signerat med Mobilt BankID
                            </div>
                          )}
                        </div>
                        <p style={{ fontSize: '0.9rem', color: '#666', margin: '5px 0 0 0' }}>
                          Datum: {agreement.seller_signed ? new Date(agreement.updated_at).toLocaleDateString('sv-SE') : '________________'}
                        </p>
                      </div>
                      
                      {/* Broker signature line */}
                      <div>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Mäklare:</p>
                        <div style={{ 
                          borderBottom: '1px solid #000', 
                          height: '30px',
                          position: 'relative',
                          marginBottom: '5px'
                        }}>
                          {agreement.broker_signed && (
                            <div style={{
                              position: 'absolute',
                              top: '5px',
                              left: '0',
                              right: '0',
                              textAlign: 'center',
                              color: '#27ae60',
                              fontWeight: 'bold'
                            }}>
                              Signerat med Mobilt BankID
                            </div>
                          )}
                        </div>
                        <p style={{ fontSize: '0.9rem', color: '#666', margin: '5px 0 0 0' }}>
                          Datum: {agreement.broker_signed ? new Date(agreement.updated_at).toLocaleDateString('sv-SE') : '________________'}
                        </p>
                      </div>
                    </div>
                    
                    {agreement.content_hash && (
                      <div style={{
                        borderTop: '1px solid #ddd',
                        paddingTop: '15px',
                        marginTop: '20px',
                        fontSize: '0.8rem',
                        color: '#666'
                      }}>
                        <p style={{ margin: '0' }}>
                          <strong>Avtalets unika dokumenthash:</strong> {agreement.content_hash}
                        </p>
                        <p style={{ margin: '5px 0 0 0', fontStyle: 'italic' }}>
                          Detta avtal är digitalt signerat med BankID och lagras i krypterad form.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {!agreement.is_fully_signed && (
                  <div 
                    ref={footerRef}
                    className={`mobile-card-footer no-print ${!isFooterVisible ? 'hide-on-scroll' : ''}`}
                    style={{ position: 'relative' }}
                  >
                    {/* Show Uppdatera Mäklaravtal button first for brokers with permission who haven't signed yet */}
                    {agreement && !agreement.is_fully_signed && 
                      (userType === 'broker' && !agreement.broker_signed && canAccessContactInfo) && (
                      <div style={{ width: '100%' }}>
                        <button 
                          onClick={() => setShowRegenerateModal(true)} 
                          className="agreement-button agreement-button-secondary"
                          style={{ 
                            width: '100%',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            fontWeight: '600'
                          }}>
                          <span>🔄</span>
                          Uppdatera Mäklaravtal
                        </button>
                      </div>
                    )}
                    
                    {!agreement.is_fully_signed && 
                      // Only show edit button if user hasn't signed yet
                      ((userType === 'seller' && !agreement.seller_signed) || 
                       (userType === 'broker' && !agreement.broker_signed)) && (
                      <button 
                        onClick={handleEdit} 
                        className="agreement-button agreement-button-warning">
                        <span>✏️</span>
                        Redigera Avtal
                      </button>
                    )}
                    
                    {userType === 'seller' && !agreement.seller_signed && (
                      <button 
                        onClick={handleSign} 
                        className="agreement-button agreement-button-primary">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
                          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                        </svg>
                        Signera med BankID
                      </button>
                    )}
                    {userType === 'broker' && !agreement.broker_signed && (
                      <button 
                        onClick={handleSign} 
                        className="agreement-button agreement-button-primary">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
                          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                        </svg>
                        Signera med BankID
                      </button>
                    )}
                    {isGenerating && <SpinnerOverlay />}
                  </div>
                )}
                
                {agreement.is_fully_signed && (
                  <div className="mobile-card">
                    <div className="mobile-card-content" style={{ 
                      backgroundColor: '#e8f8f5', 
                      borderLeft: '4px solid #27ae60',
                      color: '#27ae60',
                      fontWeight: '500',
                      textAlign: 'center'
                    }}>
                      <p style={{ margin: '0' }}>
                        <span style={{ marginRight: '8px' }}>✓</span>
                        Avtalet har blivit signerat och är nu giltigt
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Modified PDF Download button with printing indicator */}
        {agreement && !isEditing && (
          <div className="no-print" style={{ 
            marginTop: '1.5rem',
            paddingTop: '0.5rem',
            textAlign: 'center'
          }}>
            <button 
              onClick={handlePrint}
              disabled={isPrinting}
              className={`agreement-button ${isPrinting ? 'agreement-button-warning' : 'agreement-button-danger'}`}
              style={{ maxWidth: '300px', margin: '0 auto' }}>
              {isPrinting ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderRadius: '50%',
                    borderTopColor: 'transparent',
                    marginRight: '8px',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Skapar PDF...
                </>
              ) : (
                <>
                  <span>📄</span>
                  Ladda ner som PDF
                </>
              )}
            </button>
          </div>
        )}

        {/* Hidden ReactQuill instance for content normalization */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
          <ReactQuill
            ref={normalizationQuillRef}
            theme="snow"
            value=""
            readOnly={true}
            modules={{
              toolbar: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline'],
                [{'list': 'ordered'}, {'list': 'bullet'}],
                ['clean']
              ]
            }}
          />
        </div>
      </div>
    </>
  );
}

export default BrokerAgreement;
